import './ris.css';
import { type CommandId, setCommandGate } from '../commands';
import type { ViewerShell } from '../shell';
import { messageDialog } from '../ui/dialogs';
import type { ViewerSession } from './client';
import * as annotationsSync from './annotations-sync';
import * as pacsActions from './pacs-actions';
import * as preferences from './preferences';
import { hasPerm, isAuthFailure, isPatientSession, isReferrerSession, PERM, risClient, risSession, startSession } from './session';

export { isPatientSession, isReferrerSession };

/**
 * The ris workspace of the viewer: session branding (org logo, page title, favicon), the permission
 * gate of the ris commands, and the modules that talk to ris (annotation sets, preferences, captures,
 * documents, download, report). Key images live in ./keyimages (their overlay star also shows in the bench).
 */

const DEFAULT_TITLE = 'Visor';

/** lib-authentication-denied: shown when ris refuses the ticket (no login page to send the user to). */
export const AUTH_DENIED = {
  title: 'Autenticación fallida en el visor',
  text: 'Aún no se ha autentificado, por favor identifíquese primero en el sistema.',
} as const;

/** Permissions each ris command needs on top of viewer:open (viewer contract §1, §6). */
const COMMAND_PERMS: Record<string, readonly string[]> = {
  'secondary-capture': [PERM.open, PERM.annotate],
  'attach-pdf': [PERM.upload],
  'download-dicom': [PERM.export],
  'study-report': [PERM.reports],
  'share-study': [PERM.open, PERM.share],
  'key-images': [PERM.open],
  keyimage: [PERM.open],
  settings: [PERM.open],
  'reset-config': [PERM.open],
};

function allowed(id: CommandId): boolean {
  const perms = COMMAND_PERMS[id.split(':')[0]];
  return !perms || perms.every(hasPerm);
}

export type SessionStatus = 'ok' | 'denied' | 'unavailable';

export const SESSION_UNAVAILABLE = 'No fue posible conectar con el servidor del visor. Algunas funciones no están disponibles.';

/**
 * Boot step (before the feature modules build): opens the ris session, installs the permission gate of
 * the ris commands and reads the user's preferences. 'denied' = ris refused the ticket.
 */
export async function prepare(apiRoot: string, ticket: string | null | (() => string | null)): Promise<SessionStatus> {
  try {
    await startSession(apiRoot, ticket);
  } catch (error) {
    console.warn('[viewer] ris session failed', error);
    return isAuthFailure(error) ? 'denied' : 'unavailable';
  }
  setCommandGate(allowed);
  await preferences.loadPreferences();
  return 'ok';
}

export function showAuthDenied(shell: ViewerShell): void {
  messageDialog(shell, AUTH_DENIED.title, AUTH_DENIED.text, 'auth-denied-dialog', 'CERRAR');
}

function setFavicon(url: string): void {
  document.querySelectorAll('link[rel~="icon"]').forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  document.head.appendChild(link);
}

/** Page title = org name; favicon = the single logo (the patient layout uses it too). */
export function brandPage(session: ViewerSession): void {
  const client = risClient();
  const org = session.organization;
  document.title = org.name || DEFAULT_TITLE;
  if (client && org.logo_single) {
    client.logoObjectUrl(org.logo_single).then(setFavicon).catch((error: unknown) => console.warn('[viewer] favicon unavailable', error));
  }
}

/** Fills `brand` with the org logo (full, else single), else its name. */
export async function renderBrand(brand: HTMLElement, session: ViewerSession): Promise<void> {
  const client = risClient();
  const org = session.organization;
  const showName = () => {
    const name = document.createElement('span');
    name.className = 'org-name';
    name.textContent = org.name || DEFAULT_TITLE;
    brand.replaceChildren(name);
  };
  const logoPath = org.logo ?? org.logo_single;
  if (!client || !logoPath) {
    showName();
    return;
  }
  try {
    const img = document.createElement('img');
    img.className = 'org-logo';
    img.alt = org.name || DEFAULT_TITLE;
    img.src = await client.logoObjectUrl(logoPath);
    brand.replaceChildren(img);
  } catch (error) {
    console.warn('[viewer] organization logo unavailable', error);
    showName();
  }
}

/** Top bar left: the org brand goes between the "Estudios" toggle and the toolbar (the referrer layout uses it too). */
export async function applyBranding(shell: ViewerShell, session: ViewerSession): Promise<void> {
  brandPage(session);
  const brand = document.createElement('div');
  brand.id = 'org-brand';
  brand.className = 'org-brand';
  shell.regions.topNav.insertBefore(brand, shell.regions.toolbar);
  await renderBrand(brand, session);
}

/** Installed after the toolbar (the logo goes between the "Estudios" toggle and the toolbar). */
export function install(shell: ViewerShell): void {
  const session = risSession();
  if (!session) return;
  void applyBranding(shell, session);
  annotationsSync.install(shell);
  preferences.install(shell);
  pacsActions.install(shell);
}
